import { type MouseEvent, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Mail } from 'lucide-react'
import { useInView } from 'motion/react'

import { track } from '@/api/tracking'
import { formatLegalPublishedDate, getLegalDocument } from '@/features/legal/lib/legalDocuments'
import { renderLegalMarkdown } from '@/features/legal/lib/renderLegalMarkdown'
import { LocaleSeoLinks } from '@/navigation/LocaleSeoLinks'
import { Footer } from '@/shared/ui/Footer'
import { PageHelmet } from '@/shared/ui/PageHelmet'
import type { LegalDocumentId } from '@tipi/shared'

type LegalDocumentPageProps = {
    documentId: LegalDocumentId
}

export const LegalDocumentPage = ({ documentId }: LegalDocumentPageProps) => {
    const document = getLegalDocument(documentId)
    const publishedDate = formatLegalPublishedDate(document.publishedAt)
    const pageTitle = `${document.title} | Tipi`
    const description = `${document.title} de Tipi. Publicado el ${publishedDate}.`
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef)
    const navigate = useNavigate()

    useEffect(() => {
        if (isInView) {
            track('Legal Document Page', {
                document_id: documentId,
                published_at: document.publishedAt,
            })
        }
    }, [document.publishedAt, documentId, isInView])

    const handleContentClick = (event: MouseEvent<HTMLElement>) => {
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        const href = (event.target as HTMLElement).closest('a')?.getAttribute('href')
        if (!href?.startsWith('/')) return
        event.preventDefault()
        navigate(href)
    }

    return (
        <div className='flex min-h-screen flex-col bg-background text-white'>
            <LocaleSeoLinks path={document.path} locale='es' />
            <PageHelmet locale='es'>
                <title>{pageTitle}</title>
                <meta name='description' content={description} />
                <meta property='og:type' content='website' />
                <meta property='og:title' content={pageTitle} />
                <meta property='og:description' content={description} />
            </PageHelmet>
            <header className='border-neutral-200 sticky top-0 z-50 flex h-16 items-center border-b bg-black px-4 lg:px-6'>
                <Link className='flex items-center justify-center' to='/'>
                    <img src='/TIPI-tipografico.webp' alt='Tipi' className='h-16 w-auto -translate-x-1' />
                </Link>
            </header>

            <main ref={sectionRef} className='flex-1'>
                <div className='container mx-auto px-4 py-8 md:px-6 md:py-12'>
                    <div className='mb-8 max-w-4xl'>
                        <p className='text-sm text-gray-400'>Publicado el {publishedDate}</p>
                    </div>

                    <article
                        className='legal-document prose prose-invert max-w-4xl'
                        onClick={handleContentClick}
                        dangerouslySetInnerHTML={{ __html: renderLegalMarkdown(document.markdown) }}
                    />

                    <div className='mt-16 max-w-4xl rounded-lg border border-white/10 bg-white/5 p-8'>
                        <h2 className='mb-4 text-center text-2xl font-semibold'>¿Necesitas ayuda?</h2>
                        <p className='mb-4 text-center text-sm text-gray-300'>
                            Si tienes dudas sobre este documento, escríbenos y te responderemos lo antes posible.
                        </p>
                        <div className='flex justify-center'>
                            <a
                                href={`mailto:${document.contactEmail}?subject=Consulta sobre ${document.title}`}
                                className='inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90'
                            >
                                <Mail className='mr-2 h-4 w-4' />
                                Contactar
                            </a>
                        </div>
                    </div>
                </div>
            </main>

            <Footer landingPage={`legal-${documentId}`} />
        </div>
    )
}
